import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function SpotifyCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        throw new Error(`Spotify error: ${error}`);
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      const codeVerifier = localStorage.getItem('spotify_code_verifier');
      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }

      const { data, error: functionError } = await supabase.functions.invoke('spotify-oauth-callback', {
        body: { code, code_verifier: codeVerifier }
      });

      if (functionError) throw functionError;

      localStorage.removeItem('spotify_code_verifier');

      setStatus('success');
      toast({
        title: "✅ Spotify Conectado!",
        description: "Você pode agora ouvir música durante suas corridas.",
      });

      setTimeout(() => navigate('/settings/integrations'), 1500);

    } catch (error) {
      console.error('Callback error:', error);
      setStatus('error');
      toast({
        title: "Erro ao conectar",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive",
      });
      setTimeout(() => navigate('/settings/integrations'), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
            <p className="text-lg">Conectando com Spotify...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-12 h-12 mx-auto bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg text-green-600">Conectado com sucesso!</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 mx-auto bg-red-500 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-lg text-red-600">Erro ao conectar</p>
          </>
        )}
      </div>
    </div>
  );
}
